/**
 * DOM round-splash overlay: big bold "ROUND 2" / "FINAL ROUND" card that
 * pops in for ~1.5s, scales+fades, then disposes itself.
 *
 * Complements the existing Three.js round-transition card (5s, slower) by
 * giving viewers an immediate eye-grab the instant the round changes — works
 * even if the Three.js stage is mid-transition or partially occluded.
 *
 * Brand: yellow #FFC43D on blue #060CE9, Oswald display font.
 */

const STYLES = `
  @keyframes sg-splash-in {
    0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
    25%  { opacity: 1; transform: translate(-50%, -50%) scale(1.08); }
    55%  { opacity: 1; transform: translate(-50%, -50%) scale(1.0); }
    85%  { opacity: 1; transform: translate(-50%, -50%) scale(1.0); }
    100% { opacity: 0; transform: translate(-50%, -50%) scale(1.04); }
  }
  @keyframes sg-splash-shimmer {
    0%, 100% { text-shadow: 0 6px 28px rgba(0,0,0,0.7), 0 0 30px rgba(255,196,61,0.5); }
    50%      { text-shadow: 0 6px 28px rgba(0,0,0,0.7), 0 0 80px rgba(255,196,61,1.0); }
  }
  .sg-splash {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.6);
    z-index: 90;
    pointer-events: none;
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    text-align: center;
    padding: 28px 80px 32px 80px;
    background: linear-gradient(180deg, rgba(6,12,233,0.92) 0%, rgba(3,4,40,0.96) 100%);
    border: 4px solid #FFC43D;
    border-radius: 18px;
    box-shadow: 0 12px 60px rgba(0,0,0,0.7), 0 0 80px rgba(255,196,61,0.4);
    color: #fff;
    animation: sg-splash-in 1500ms cubic-bezier(.18,.85,.4,1.0) both;
  }
  .sg-splash-title {
    font-size: 140px;
    line-height: 0.95;
    font-weight: 700;
    color: #FFC43D;
    letter-spacing: 0.06em;
    animation: sg-splash-shimmer 1.4s ease-in-out infinite;
  }
  .sg-splash-sub {
    margin-top: 8px;
    font-size: 28px;
    letter-spacing: 0.32em;
    color: #E2E8F0;
    text-transform: uppercase;
    opacity: 0.95;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('sg-splash-style')) return;
  const s = document.createElement('style');
  s.id = 'sg-splash-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export interface RoundSplashHandle {
  show: (title: string, subtitle?: string) => void;
  dispose: () => void;
}

export function createRoundSplash(mount: HTMLElement): RoundSplashHandle {
  ensureStyles();
  let active: HTMLDivElement | null = null;
  let timer: number | null = null;

  function show(title: string, subtitle?: string): void {
    // Replace any in-flight splash so back-to-back calls don't stack.
    if (active) active.remove();
    if (timer !== null) clearTimeout(timer);
    const el = document.createElement('div');
    el.className = 'sg-splash';
    el.setAttribute('data-overlay', 'round-splash');
    el.innerHTML = `
      <div class="sg-splash-title">${escapeHtml(title)}</div>
      ${subtitle ? `<div class="sg-splash-sub">${escapeHtml(subtitle)}</div>` : ''}
    `;
    mount.appendChild(el);
    active = el;
    // Match the CSS animation duration (1500ms).
    timer = window.setTimeout(() => {
      el.remove();
      if (active === el) active = null;
      timer = null;
    }, 1500);
  }

  return {
    show,
    dispose() {
      if (timer !== null) clearTimeout(timer);
      if (active) active.remove();
      active = null;
    },
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
